import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { CompanySummary } from '../models/company-summary';

export interface TechnicalPopSummary {
  id: number;
  uuid?: string;
  technicalId?: string;
  name?: string;
}

export interface CrossConnect {
  crossConnectId: string;
  technicalPopUuid?: string | null;
  technicalPop?: TechnicalPopSummary | null;
  selfHosted?: boolean | null;
  nameThirdPartyHost?: string | null;
  vlanId?: string | null;
  datacenterRef?: string | null;
}

export interface BandwidthEndpoint {
  popId: number;
  popDisplayId?: string | null;
  popName?: string | null;
  crossConnectId: string;
  technicalPop?: TechnicalPopSummary | null;
  mode?: string | null;
}

export interface BandwidthDetails {
  role: 'injection' | 'reception';
  contractReference?: string | null;
  scheduleName?: string | null;
  bundleName?: string | null;
  etreeId?: string | null;
  vvlanId?: number | null;
  bandwidth?: string | null;
  vlan?: string | null;
  injection?: BandwidthEndpoint | null;
  receptions: BandwidthEndpoint[];
  injectionMethod?: string | null;
  injectionMode?: string | null;
  injectionBandwidth?: string | null;
  receptionMode?: string | null;
}

export interface MulticastDetail {
  id: number;
  port?: string | null;
  multicastAddress?: string | null;
  platform?: string | null;
  connectivity?: string | null;
}

export interface ChannelDetail {
  channelId?: number | null;
  crossConnectId?: string | null;
  channelCode?: string | null;
  permissioningId?: string | null;
  permissioningIp?: string | null;
  port?: string | null;
  platform?: string | null;
  layer?: string | null;
  configStateStatus?: string | null;
  configStateAppliedDate?: string | null;
}

export interface SymbolDetail {
  assetClass?: string | null;
  name?: string | null;
  maturity?: string | null;
  symbol?: string | null;
  product?: string | null;
  service?: string | null;
  marketName?: string | null;
  market?: string | null;
  channelId?: number | null;
}

export interface ServiceHistory {
  type: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface ServiceItem {
  id: number;
  uuid?: string | null;
  status: string;
  service: string;
  product: {
    id?: number | null;
    uuid?: string | null;
    name?: string | null;
    service?: string | null;
    carrier?: string | null;
    addpSelfServe?: boolean;
  };
  selfHosted: boolean;
  thirdPartyHost?: string | null;
  isEncrypted?: boolean | null;
  interface?: string | null;
  configuredDate?: string | null;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  crossConnects: CrossConnect[];
  channels: ChannelDetail[];
  symbols: SymbolDetail[];
  histories?: ServiceHistory[];
  symbolsLoaded?: boolean;
  symbolsLoading?: boolean;
  orderForm?: {
    id?: number | null;
    currency?: string | null;
    organizationId?: string | null;
    contractingAccountId?: string | null;
    msaId?: number | null;
    msaUuid?: string | null;
    msaName?: string | null;
  } | null;
  bandwidthDetails?: BandwidthDetails | null;
}

export interface BillingAccountSummary {
  id: number;
  uuid?: string | null;
  name: string;
  shortname?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface AddpContactInfo {
  id: number;
  name: string;
  email: string;
  daytimePhone?: string | null;
  mobilePhone?: string | null;
  phone?: string | null;
}

export interface AddpSuggestedContacts {
  primary?: AddpContactInfo | null;
  technical?: AddpContactInfo | null;
  billing?: AddpContactInfo | null;
  dataUsage?: AddpContactInfo | null;
  notification?: AddpContactInfo | null;
}

export interface AddpCompanyDetails {
  company: CompanySummary;
  suggestedContacts: AddpSuggestedContacts;
  billingAccounts: BillingAccountSummary[];
  mckayEntities: string[];
  mckayEntityCatalog: { uuid: string | null; name: string | null }[];
}

export interface ServiceDetail {
  key: string;
  label: string;
  items: ServiceItem[];
  symbolsExpanded?: boolean;
}

export interface PopSummary {
  id: number;
  uuid?: string | null;
  displayId?: string | null;
  name: string;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  country?: string | null;
  continent?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  availableServices: string[];
  services: string[];
  serviceCounts: Record<string, number>;
  technicalPops: TechnicalPopSummary[];
  crossConnects: CrossConnect[];
}

export interface ServiceDeliveryLocationsResponse {
  company: CompanySummary;
  locations: PopSummary[];
  /**
   * Legacy key kept temporarily while backend/front transition is underway.
   */
  pops?: PopSummary[];
}

export interface ServiceDeliveryLocationDetails {
  company: CompanySummary;
  commercialPop: {
    id: number;
    uuid?: string | null;
    displayId?: string | null;
    name: string;
    city?: string | null;
    state?: string | null;
    region?: string | null;
    country?: string | null;
    continent?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    availableServices: string[];
    addpSelfServe?: boolean;
  };
  services: ServiceDetail[];
}

@Injectable({
  providedIn: 'root'
})
export class ServiceDeliveryLocationsService {

  private readonly baseUrl = environment.baseUrlWs;

  constructor(private http: HttpClient) { }

  getCompanyLocations(companyId: number): Observable<ServiceDeliveryLocationsResponse> {
    return this.http.get<ServiceDeliveryLocationsResponse>(`${this.baseUrl}/api/service-delivery-locations/companies/${companyId}/locations`);
  }

  getLocationDetails(companyId: number, locationId: number): Observable<ServiceDeliveryLocationDetails> {
    return this.http.get<ServiceDeliveryLocationDetails>(`${this.baseUrl}/api/service-delivery-locations/companies/${companyId}/locations/${locationId}`);
  }

  getItemSymbols(companyId: number, locationId: number, itemId: number, channelIds: number[]): Observable<SymbolDetail[]> {
    let params = new HttpParams();
    channelIds.forEach(id => {
      params = params.append('channelIds[]', String(id));
    });
    return this.http.get<SymbolDetail[]>(
      `${this.baseUrl}/api/service-delivery-locations/companies/${companyId}/locations/${locationId}/items/${itemId}/symbols`,
      { params }
    );
  }

  getAddpCompanyDetails(companyId: number): Observable<AddpCompanyDetails> {
    return this.http.get<AddpCompanyDetails>(
      `${this.baseUrl}/api/service-delivery-locations/companies/${companyId}/addp/details`
    );
  }
}
